import React, { useEffect, useState } from 'react'
import { Helmet } from '@dr.pogodin/react-helmet'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { normalizedWarrantName } from '@finance-lab/utils/shared'
import { WatchListEntity, WatchListItem } from 'types/WatchListEntity'
import { useLogUserEvent } from '../../hooks/useLogUserEvent'
import UserEventApi from '../../api/UserEventApi'
import { WatchListItemType, WatchListsApi } from '../../api/WatchListApi'
import { WatchListGroup } from './WatchListGroup'
import { WatchListButton } from '../components/WatchListButton'

const watchListApi = new WatchListsApi()

const groupWatchListItems = (watchList: WatchListEntity | null) =>
    (watchList?.items || []).reduce(
        (acc, item) => {
            const type =
                item.issueId !== null
                    ? 'issue'
                    : item.warrantId !== null
                      ? 'warrant'
                      : item.companyId !== null
                        ? 'company'
                        : item.investorId !== null
                          ? 'investor'
                          : item.advisorId !== null
                            ? 'advisor'
                            : item.convertibleId !== null
                              ? 'convertible'
                              : null

            if (type === null) {
                console.error(`Unknown watch item type: `, item)
                return acc
            }

            const name = type === 'warrant' ? normalizedWarrantName(item) : item.name

            return {
                ...acc,
                [type]: [...(acc[type] || []), { ...item, name: name || '-' }].sort((a, b) =>
                    (a.name as string).localeCompare(b.name as string),
                ),
            }
        },
        { company: [], investor: [], issue: [], warrant: [], convertible: [], advisor: [] } as Record<
            WatchListItemType,
            WatchListItem[]
        >,
    )

export const WatchListView = () => {
    const [watchList, setWatchList] = useState<WatchListEntity | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        void (async () => {
            const [result] = await watchListApi.getAll()

            setWatchList(result)
            setIsLoading(false)
        })()
    }, [])

    useLogUserEvent(UserEventApi.EVENT_TYPES.SHOW_WATCH_LIST)

    const groupedItems = groupWatchListItems(watchList)
    const groups = Object.keys(groupedItems) as WatchListItemType[]

    const handleChange = async () => {
        const [result] = await watchListApi.getAll()

        setWatchList(result)
    }

    return (
        <>
            <Helmet title={'Bevakningslista'} />
            <Typography variant='h3' gutterBottom display='inline'>
                Bevakningslista
                <WatchListButton disabled isInWatchList />
            </Typography>

            <div style={{ maxWidth: 800 }}>
                <p>
                    Nedan kan du hantera din bevakningslista. Genom att lägga till bevakningar har du möjlighet att
                    filtrera nyheter, kalender, emissioner, teckningsoptioner och bolag baserat på dina val.
                </p>

                <p>
                    Funktionen är under utveckling, hör gärna av dig med feedback till din kontaktperson på Finance Lab.
                </p>
            </div>

            <Divider sx={{ my: 6 }} />

            <div style={{ maxWidth: 1200 }}>
                {groups &&
                    groupedItems &&
                    groups.map((key) => (
                        <WatchListGroup
                            key={key}
                            isLoading={isLoading}
                            items={groupedItems[key]}
                            onChange={handleChange}
                            type={key}
                        />
                    ))}
            </div>
        </>
    )
}
